// @flow
import React, { type Node } from 'react';
import { useTranslation } from 'react-i18next';

import Person from 'app/common/classes/person';
import PlainButton from 'app/common/ui-next/plain-button';
import ControlsBar from 'app/common/ui-next/controls-bar';
import SimpleDate from 'app/common/classes/simple-date';
import PlainIcon, { iconTypes, colorTypes } from 'app/common/ui-next/plain-icon';

import OrderModal from './OrderModal';
import ExchangeModal from './ExchangeModal';
import PutOnFood from './PutOnFood/PutOnFood';
import RemoveFromFood from './RemoveFromFood/RemoveFromFood';
import ChangeFood from './ChangeFood/ChangeFood';
import ExchangeComplex from './ExchangeComplex/ExchangeComplex';
import type { GroupType } from '../../../dataTypes/orderRequests/groupTypes';

import styles from './ProductListFooter.module.scss';;


type Props = {
  putOnFoodOption: boolean,
  removeFromFoodOption: boolean,
  changeOption: boolean,
  exchangeComplexOption: boolean,
  customer: Person,
  date: SimpleDate,
  groupType: GroupType | null,
  orderRequestId: string,
  changeId: string,
  onUpdate: () => Promise<void> | void,
  foodModeTitle: string,
  putOnFoodOnlySingleComplex: boolean,
}

const ProductListFooter = ({
  putOnFoodOption,
  removeFromFoodOption,
  changeOption,
  exchangeComplexOption,
  customer,
  date,
  groupType,
  orderRequestId,
  changeId,
  onUpdate,
  foodModeTitle,
  putOnFoodOnlySingleComplex,
}: Props): Node => {
  const { t } = useTranslation('cafeteria');
  const whom = customer.kogoChego.first;
  const whomAccusative = customer.kogoChto.first;

  return (
    <div className={styles.root}>
      <ControlsBar right>

        {changeOption
          && <ChangeFood
            customerId={customer.id}
            orderId={orderRequestId}
            whom={whom}
          >
            {({ errorProcessing, header, successMessage, requestOrderInitialItems }) => (
              <OrderModal
                header={header}
                date={date}
                groupType={groupType}
                customer={customer}
                orderRequestId={orderRequestId}
                changeId={changeId}
                successMessage={successMessage}
                onOrderSubmitSuccess={onUpdate}
                errorProcessing={errorProcessing}
                requestOrderInitialState={requestOrderInitialItems}
              >
                {onClick => (
                  <PlainButton onClick={onClick} warning>
                    {t('order_request_sales_change_bt', 'Изменить')}
                    &nbsp;
                    <PlainIcon
                      width="auto"
                      height="18px"
                      type={iconTypes.CUTLERY}
                      colorType={colorTypes.COLOR_WHITE}
                    />
                  </PlainButton>
                )}
              </OrderModal>
            )}
          </ChangeFood>}

        {exchangeComplexOption
          && <ExchangeComplex
            foodModeTitle={foodModeTitle}
            date={date}
            customerId={customer.id}
            orderId={orderRequestId}
          >
            {({ errorProcessing, header, successMessage, requestOrderInitialItems }) => (
              <ExchangeModal
                header={header}
                date={date}
                groupType={groupType}
                customer={customer}
                orderRequestId={orderRequestId}
                changeId={changeId}
                successMessage={successMessage}
                errorProcessing={errorProcessing}
                onOrderSubmitSuccess={onUpdate}
                requestOrderInitialState={requestOrderInitialItems}
              >
                {onClick => (
                  <PlainButton onClick={onClick} warning>
                    {t('order_request_sales_exchange_complex_bt', 'Заменить комплект')}
                    &nbsp;
                    <PlainIcon
                      width="auto"
                      height="18px"
                      type={iconTypes.CUTLERY}
                      colorType={colorTypes.COLOR_WHITE}
                    />
                  </PlainButton>
                )}
              </ExchangeModal>
            )}
          </ExchangeComplex>}

        {removeFromFoodOption
          && <RemoveFromFood
            customer={customer}
            orderRequestId={orderRequestId}
            changeId={changeId}
            onRemoveFromFoodSuccess={onUpdate}
          >
            {toggle => (
              <PlainButton onClick={toggle} danger>
                {t('order_request_sales_remove_from_food_bt', { whom: whomAccusative })}
                &nbsp;
                <PlainIcon
                  width="auto"
                  height="18px"
                  type={iconTypes.CROSS_CIRCLE}
                  colorType={colorTypes.COLOR_WHITE}
                />
              </PlainButton>
            )}
          </RemoveFromFood>}

        {putOnFoodOption
          && <PutOnFood whom={whom} whomAccusative={whomAccusative}>
            {({ errorProcessing, header, successMessage }) => (
              <OrderModal
                header={header}
                date={date}
                groupType={groupType}
                customer={customer}
                orderRequestId={orderRequestId}
                changeId={changeId}
                successMessage={successMessage}
                onOrderSubmitSuccess={onUpdate}
                errorProcessing={errorProcessing}
                onlySingleComplex={putOnFoodOnlySingleComplex}
              >
                {onClick => (
                  <PlainButton onClick={onClick} success>
                    {t('order_request_sales_put_on_food_bt', { whom: whomAccusative })}
                    &nbsp;
                    <span className={styles.iconLetter}>{'+'}</span>
                  </PlainButton>
                )}
              </OrderModal>
            )}
          </PutOnFood>}

      </ControlsBar>
    </div>
  );
};

export default ProductListFooter;
